import { create } from 'zustand';

export type CoachId = string;

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'ai';
  coachId?: string;
  timestamp: Date;
  liked?: boolean;
  saved?: boolean;
  thumbsUp?: boolean;
  thumbsDown?: boolean;
  source?: 'integrated' | 'coach';
  coachBreakdowns?: Array<{ coach: string; text: string; }>;
  isError?: boolean;
  reactionCount?: {
    likes: number;
    thumbsUp: number;
    thumbsDown: number;
    saves: number;
  };
}

interface CoachingSessionState {
  selectedCoachIds: CoachId[];
  activeCoachId: CoachId | null;
  sessionId: string | null;
  sessionTitle: string | null;
  messages: Message[];
  likedMessageIds: string[];
  setSelectedCoachIds: (ids: CoachId[]) => void;
  toggleCoach: (id: CoachId) => void;
  setActiveCoachId: (id: CoachId) => void;
  setSessionMeta: (payload: { sessionId?: string | null; title?: string | null; coachIds?: CoachId[] }) => void;
  setSessionData: (sessionId: string, sessionTitle?: string) => void;
  setMessages: (messages: Message[] | ((prev: Message[]) => Message[])) => void;
  addMessage: (message: Message) => void;
  addMessages: (messages: Message[]) => void;
  setLikedMessageIds: (ids: string[]) => void;
  reset: () => void;
}

const MAX_COACHES = 3;

export const useCoachingSessionStore = create<CoachingSessionState>((set) => ({
  selectedCoachIds: [],
  activeCoachId: null,
  sessionId: null,
  sessionTitle: null,
  messages: [],
  likedMessageIds: [],
  setSelectedCoachIds: (ids) => {
    set((state) => {
      const unique = Array.from(new Set(ids)).slice(0, MAX_COACHES);
      const nextActive = unique.includes(state.activeCoachId ?? '')
        ? state.activeCoachId
        : unique[0] ?? null;
      return {
        selectedCoachIds: unique,
        activeCoachId: nextActive,
      };
    });
  },
  toggleCoach: (id) => {
    set((state) => {
      const alreadySelected = state.selectedCoachIds.includes(id);
      if (alreadySelected) {
        if (state.selectedCoachIds.length <= 1) {
          return state;
        }
        const nextIds = state.selectedCoachIds.filter((coachId) => coachId !== id);
        const nextActive = nextIds.includes(state.activeCoachId ?? '')
          ? state.activeCoachId
          : nextIds[0] ?? null;
        return {
          selectedCoachIds: nextIds,
          activeCoachId: nextActive,
        };
      }
      if (state.selectedCoachIds.length >= MAX_COACHES) {
        return state;
      }
      const nextIds = [...state.selectedCoachIds, id];
      return {
        selectedCoachIds: nextIds,
        activeCoachId: state.activeCoachId ?? id,
      };
    });
  },
  setActiveCoachId: (id) => {
    set((state) => {
      if (!state.selectedCoachIds.includes(id)) {
        return state;
      }
      return { activeCoachId: id };
    });
  },
  setSessionMeta: ({ sessionId, title, coachIds }) => {
    set((state) => {
      const nextCoachIds = coachIds
        ? Array.from(new Set(coachIds)).slice(0, MAX_COACHES)
        : state.selectedCoachIds;
      const nextActive = nextCoachIds.includes(state.activeCoachId ?? '')
        ? state.activeCoachId
        : nextCoachIds[0] ?? null;
      return {
        sessionId: sessionId ?? state.sessionId,
        sessionTitle: title ?? state.sessionTitle,
        selectedCoachIds: nextCoachIds,
        activeCoachId: nextActive,
      };
    });
  },
  setSessionData: (sessionId, sessionTitle) => {
    set({ sessionId, sessionTitle });
  },
  setMessages: (messagesOrFn) => set((state) => ({
    messages: typeof messagesOrFn === 'function' ? messagesOrFn(state.messages) : messagesOrFn
  })),
  addMessage: (message) => set((state) => ({
    messages: [...state.messages, message]
  })),
  addMessages: (messages) => set((state) => ({
    messages: [...state.messages, ...messages]
  })),
  setLikedMessageIds: (ids) => set({ likedMessageIds: ids }),
  reset: () => set({
    selectedCoachIds: [],
    activeCoachId: null,
    sessionId: null,
    sessionTitle: null,
    messages: [],
    likedMessageIds: [],
  }),
}));
